import { PERMISSION } from '../../../components/investigation/share/user_roles';
import { FUNCTIONALITY } from '../../../constants/types';
import {
    CHANGE_PASSWORD_ROUTE, HOSPITAL_HOME_ROUTE, HOSPITAL_MY_CORRECTIONS, HOSPITAL_PATIENT, ROOT_ROUTE,
} from '../../../routes/urls';

/** The investigations list (`/investigations/all|live|pending|draft`), where invitations are answered. */
const INVESTIGATIONS_PAGES = '/investigations';

/** `/patient/`: the patient page and its sub-routes (the section edit among them). */
const PATIENT_PAGES = HOSPITAL_PATIENT.slice(0, HOSPITAL_PATIENT.indexOf(':'));

/**
 * SKIN-600 — who may use the correction flow on the web, and where a dermatologist's web
 * session may go. One module, so the shell, the menu, the patient page, photo placement and
 * the edit button cannot each decide differently.
 *
 * The API is the boundary (sherwood-api `docs/features/quality-check/correction-permission.md`):
 * a web token of a researcher holding the system DERMATOLOGIST role in any accepted study
 * reaches only its correction work, and anything else answers `403 WEB_SESSION_OUT_OF_SCOPE`.
 * Everything here mirrors that so the dashboard never offers what the API will refuse.
 *
 * The client is not told its role, only its permissions per study, so it reads the role off the
 * SkincAIr system catalogue (sherwood-api SKINCAIR_ROLES). Since v5.18.3 only SHERWOOD_STAFF holds
 * APPROVE_VISIT, so APPROVE_VISIT no longer tells SUPER_ADMIN from the dermatologist (SKIN-785).
 * What does: of the three roles that correct flagged visits (SHERWOOD_STAFF, SUPER_ADMIN,
 * DERMATOLOGIST), the dermatologist alone does not resolve draft visits. So
 * CORRECT_FLAGGED_VISITS without RESOLVE_DRAFT_VISITS stands in for DERMATOLOGIST, and with it for
 * the API's system administrators (SUPER_ADMIN, SHERWOOD_STAFF). A change to that catalogue has to
 * be checked against this pair; `correctionAccess.test.ts` models it role by role.
 */

/** The fields of an `investigation/all` entry this module reads. */
export interface StudyAccess {
    permissions?: string[];
    functionalities?: string[];
    shareStatus?: number | null;
}

/** sherwood-api `InvestigationShareStatus.ACCEPTED`: the API counts accepted memberships only. */
const SHARE_ACCEPTED = 2;

const holds = (study: StudyAccess | null | undefined, permission: PERMISSION) =>
    !!study?.permissions?.includes(permission);

const hasCorrectionFunctionality = (study: StudyAccess | null | undefined) =>
    !!study?.functionalities?.includes(FUNCTIONALITY.WEB_CLINICAL_CORRECTION);

/** The dermatologist: corrects its own flagged visits, and resolves no drafts. */
function correctsOnlyFlaggedVisits(study: StudyAccess | null | undefined): boolean {
    return holds(study, PERMISSION.CORRECT_FLAGGED_VISITS) && !holds(study, PERMISSION.RESOLVE_DRAFT_VISITS);
}

/**
 * Whether this web session is a dermatologist's, limited by the API to its correction work.
 * Like the API, one accepted study as a dermatologist limits the session in every study.
 */
export function isCorrectionOnlySession(studies: StudyAccess[] | null | undefined): boolean {
    return !!studies?.some((study) => study.shareStatus === SHARE_ACCEPTED && correctsOnlyFlaggedVisits(study));
}

/** The panel's gate: the list endpoint needs both. */
export function canOpenCorrections(study: StudyAccess | null | undefined): boolean {
    return hasCorrectionFunctionality(study) && holds(study, PERMISSION.CORRECT_FLAGGED_VISITS);
}

/**
 * The API's system administrators (SUPER_ADMIN, SHERWOOD_STAFF) see every open flag of the study
 * (and filter by assignee); the dermatologist its own.
 */
export function seesStudyWideCorrections(study: StudyAccess | null | undefined): boolean {
    return holds(study, PERMISSION.CORRECT_FLAGGED_VISITS) && holds(study, PERMISSION.RESOLVE_DRAFT_VISITS);
}

/**
 * Whether the visits with an open flag this caller may correct have to be loaded for
 * `mayCorrectVisit`: everyone who corrects flagged visits without Quality Check. The list endpoint
 * answers each with what the API lets them correct — the whole study to a system administrator,
 * their own flags to the dermatologist. Quality Check (APPROVE_VISIT) corrects any SUBMITTED
 * visit and needs no list.
 */
export function loadsOpenFlagVisits(study: StudyAccess | null | undefined): boolean {
    return canOpenCorrections(study) && !holds(study, PERMISSION.APPROVE_VISIT);
}

/**
 * Whether the correction tools (submission edit on a closed visit, photo placement) are offered
 * on this visit. The API's `canCorrectVisit`: APPROVE_VISIT on any SUBMITTED visit, or
 * CORRECT_FLAGGED_VISITS on a SUBMITTED visit with an open flag the caller may correct — assigned
 * to it, or any for a system administrator — which is what `ownOpenFlagVisits` lists (null while
 * it is unknown: nothing is offered then).
 */
export function mayCorrectVisit(
    study: StudyAccess | null | undefined,
    visit: { uuid?: string | null; status?: string | null } | null | undefined,
    ownOpenFlagVisits: ReadonlySet<string> | null,
): boolean {
    if (!hasCorrectionFunctionality(study) || !visit?.uuid || visit.status !== 'SUBMITTED') return false;
    if (holds(study, PERMISSION.APPROVE_VISIT)) return true;
    return holds(study, PERMISSION.CORRECT_FLAGGED_VISITS) && !!ownOpenFlagVisits?.has(visit.uuid);
}

/**
 * What a dermatologist web session does on `pathname`:
 * - `allow`: render it;
 * - `panel`: send it to My corrections;
 * - `home`: send it to the home route, where it picks a study;
 * - `block`: the "your work is in the mobile app" screen — a study without the functionality,
 *   or one where it is not a corrector, where the API refuses everything but the session.
 *
 * The invitations and the forced password change stay reachable whatever the study.
 */
export type CorrectionShellRoute = 'allow' | 'panel' | 'home' | 'block';

export function correctionShellRoute({ pathname, study, hasPendingInvitation }: {
    pathname: string;
    study: StudyAccess | null | undefined;
    hasPendingInvitation: boolean;
}): CorrectionShellRoute {
    if (pathname === CHANGE_PASSWORD_ROUTE || pathname.startsWith(INVESTIGATIONS_PAGES)) return 'allow';
    const onHome = pathname === ROOT_ROUTE || pathname === HOSPITAL_HOME_ROUTE;
    if (onHome && (hasPendingInvitation || !study)) return 'allow';
    if (!study) return 'home';
    if (!canOpenCorrections(study)) return 'block';
    return pathname === HOSPITAL_MY_CORRECTIONS || pathname.startsWith(PATIENT_PAGES) ? 'allow' : 'panel';
}
